import React, { useEffect, useMemo, useState } from "react";
import { FeatherPlus, FeatherTrash2, FeatherX } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { TextArea } from "../../../../ui/components/TextArea";
import { CashBankTransactionEntity } from "../../domain";
import {
  formatCashBankAmount,
  formatCashBankDate,
} from "../utils/cashBankFormatters";

interface SplitTransactionLine {
  amount: number;
  description: string;
}

interface SplitTransactionModalProps {
  open: boolean;
  transaction: CashBankTransactionEntity | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (splits: SplitTransactionLine[]) => Promise<void>;
}

const toMinorUnits = (amount: number) => Math.round(amount * 100);

const createEmptyLine = (): SplitTransactionLine => ({
  amount: 0,
  description: "",
});

export const SplitTransactionModal: React.FC<SplitTransactionModalProps> = ({
  open,
  transaction,
  onOpenChange,
  onSubmit,
}) => {
  const [lines, setLines] = useState<SplitTransactionLine[]>([
    createEmptyLine(),
    createEmptyLine(),
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLines([createEmptyLine(), createEmptyLine()]);
      setSubmitError(null);
      setSubmitting(false);
    }
  }, [open, transaction?.transaction_id]);

  const originalMinorAmount = useMemo(
    () => toMinorUnits(transaction?.amount ?? 0),
    [transaction?.amount],
  );
  const splitMinorTotal = useMemo(
    () => lines.reduce((total, line) => total + toMinorUnits(line.amount), 0),
    [lines],
  );
  const remainingMinorAmount = originalMinorAmount - splitMinorTotal;

  const hasMissingDescription = lines.some(
    (line) => line.description.trim() === "",
  );
  const hasInvalidAmount = lines.some(
    (line) => !Number.isFinite(line.amount) || line.amount <= 0,
  );
  const isTotalValid = splitMinorTotal === originalMinorAmount;
  const canSubmit =
    Boolean(transaction) &&
    lines.length >= 2 &&
    !hasMissingDescription &&
    !hasInvalidAmount &&
    isTotalValid &&
    !submitting;

  if (!open || !transaction) {
    return null;
  }

  const updateLine = (
    index: number,
    field: keyof SplitTransactionLine,
    value: string,
  ) => {
    if (field === "amount" && !/^\d*\.?\d{0,2}$/.test(value)) {
      return;
    }

    setLines((currentLines) =>
      currentLines.map((line, lineIndex) => {
        if (lineIndex !== index) {
          return line;
        }

        return {
          ...line,
          [field]: field === "amount" ? Number(value || 0) : value,
        };
      }),
    );
  };

  const addLine = () => {
    setLines((currentLines) => [...currentLines, createEmptyLine()]);
  };

  const removeLine = (index: number) => {
    setLines((currentLines) =>
      currentLines.length <= 2
        ? currentLines
        : currentLines.filter((_, lineIndex) => lineIndex !== index),
    );
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      setSubmitError("Split rows must total the original amount exactly.");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      await onSubmit(
        lines.map((line) => ({
          amount: toMinorUnits(line.amount) / 100,
          description: line.description.trim(),
        })),
      );
      onOpenChange(false);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Failed to split transaction",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const totalHelpText = isTotalValid
    ? "Split total matches the original transaction."
    : remainingMinorAmount > 0
      ? `Remaining: ${formatCashBankAmount(remainingMinorAmount / 100)}`
      : `Over by: ${formatCashBankAmount(remainingMinorAmount / 100)}`;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 px-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-default-background shadow-xl">
        <div className="flex items-center justify-between gap-4 border-b border-solid border-neutral-border px-6 py-4">
          <div className="flex flex-col gap-1">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Split Transaction
            </span>
            <span className="text-body font-body text-subtext-color">
              {formatCashBankDate(transaction.date)} ·{" "}
              {formatCashBankAmount(transaction.amount)}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-end gap-1">
              <span className="text-caption font-caption text-subtext-color">
                Split total
              </span>
              <span className="text-body-bold font-body-bold text-default-font">
                {formatCashBankAmount(splitMinorTotal / 100)}
              </span>
              <span
                className={`text-caption font-caption ${
                  isTotalValid ? "text-success-600" : "text-error-600"
                }`}
              >
                {totalHelpText}
              </span>
            </div>
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherX />}
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 overflow-auto px-6 py-5">
          <div className="rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
            <span
              className="text-body-bold font-body-bold text-default-font"
              title={transaction.description}
            >
              {transaction.description}
            </span>
          </div>

          <div className="flex flex-col gap-3">
            {lines.map((line, index) => {
              return (
                <div
                  key={index}
                  className="grid grid-cols-[160px_1fr_auto] items-start gap-3 rounded-md border border-solid border-neutral-border p-3"
                >
                  <TextField
                    label={
                      <span>
                        Amount {index + 1} <span className="text-error-600">*</span>
                      </span>
                    }
                  >
                    <TextField.Input
                      type="text"
                      inputMode="decimal"
                      value={line.amount > 0 ? String(line.amount) : ""}
                      onChange={(event) =>
                        updateLine(index, "amount", event.target.value)
                      }
                    />
                  </TextField>

                  <TextArea
                    label={
                      <span>
                        Description <span className="text-error-600">*</span>
                      </span>
                    }
                  >
                    <TextArea.Input
                      value={line.description}
                      placeholder="Enter split transaction description"
                      onChange={(event) =>
                        updateLine(index, "description", event.target.value)
                      }
                    />
                  </TextArea>

                  <Button
                    variant="destructive-tertiary"
                    size="small"
                    icon={<FeatherTrash2 />}
                    onClick={() => removeLine(index)}
                    disabled={lines.length <= 2 || submitting}
                    className="mt-5"
                  />
                </div>
              );
            })}
          </div>

          <Button
            variant="brand-tertiary"
            size="small"
            icon={<FeatherPlus />}
            onClick={addLine}
            disabled={submitting}
          >
            Add Split Row
          </Button>

          {submitError && (
            <span className="text-body font-body text-error-600">
              {submitError}
            </span>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-solid border-neutral-border px-6 py-4">
          <Button
            variant="neutral-tertiary"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button loading={submitting} disabled={!canSubmit} onClick={handleSubmit}>
            Split
          </Button>
        </div>
      </div>
    </div>
  );
};
